import { parseDateFromSdmxPeriod } from '../lib/sdmx/frequency';
import { groupBy, has, isEmpty, isNil, omit } from './fp';

const PERIOD = 'PERIOD';
export const LASTNOBSERVATIONS = 'LASTNOBSERVATIONS';
export const LASTNPERIODS = 'LASTNPERIODS';
export const START_PERIOD = 'START_PERIOD';
export const END_PERIOD = 'END_PERIOD';

// insert separator between each element of list (like R.intersperse)
const intersperse = (separator, list) =>
  list.reduce((acc, item, index) => (index === 0 ? [item] : [...acc, separator, item]), []);

export const getSelectedValuesWithPath = filters =>
  filters.map(filter => {
    const grouped = groupBy(value => value.id, filter.values);
    const { filtered, indexed } = filter.values.reduce(
      (acc, val) => {
        const value =
          grouped[val.id].length > 1
            ? {
                ...val,
                parentId: undefined,
                parents: grouped[val.id].map(item => [item]),
              }
            : val;
        if (has(val.id, acc.registered)) {
          return {
            ...acc,
            indexed: { ...acc.indexed, [value.hierarchicalId || value.id]: value },
          };
        }
        return {
          filtered: value.isSelected ? [...acc.filtered, value] : acc.filtered,
          registered: { ...acc.registered, [value.id]: value },
          indexed: { ...acc.indexed, [value.hierarchicalId || value.id]: value },
        };
      },
      { filtered: [], indexed: {}, registered: {} },
    );

    const getParents = value => {
      return value.parents
        ? [
            {
              ...value,
              parents: value.parents.reduce((acc, parent) => {
                const ancestorId = parent[0]?.parentId;
                if (isNil(ancestorId) || !has(ancestorId, indexed)) {
                  return acc;
                }
                const res = [...getParents(indexed[ancestorId]), omit(['parents'], value)];
                return [...acc, res];
              }, []),
            },
          ]
        : isNil(value.parentId) || !has(value.parentId, indexed)
          ? [value]
          : [...getParents(indexed[value.parentId]), value];
    };

    const values = filtered.map(getParents);
    return { ...filter, values };
  });

export const getUsedFilterPeriod = (
  frequency,
  period,
  lastNObs,
  labelRenderer,
  timePeriod,
  lastNMode,
) => {
  const getLastNValues = lastNObs => {
    if (
      lastNObs === '0' ||
      isNil(lastNObs) ||
      isEmpty(lastNObs) ||
      isEmpty(lastNMode) ||
      isNil(lastNMode)
    )
      return [];
    return [
      [
        {
          id: LASTNOBSERVATIONS,
          label: lastNObs,
        },
      ],
    ];
  };

  const getPeriodsValues = (period, labelRenderer) => {
    const dates = parseDateFromSdmxPeriod(frequency, isNil(period) ? [] : period);
    const ids = [START_PERIOD, END_PERIOD];
    return dates.reduce((acc, period, index) => {
      if (isNil(period)) return acc;
      return [
        ...acc,
        [
          {
            id: `${ids[index]}`,
            label: `${labelRenderer(period)}`,
          },
        ],
      ];
    }, []);
  };
  const values = [...getPeriodsValues(period, labelRenderer), ...getLastNValues(lastNObs)];
  return isEmpty(values) ? [] : [{ id: PERIOD, label: timePeriod?.name, values }];
};

export const getUsedFilterFrequency = (frequencyType, frequencyOptions) => freqDimension => {
  const option = (freqDimension?.values ?? []).find(value => value.id === frequencyType);
  const label = option && (option.display ?? true) ? frequencyOptions[frequencyType] : null;
  if (isNil(label)) return [];
  return [
    {
      ...freqDimension,
      label: freqDimension.name,
      isNotRemovable: true,
      values: [[{ id: 'frequency_type', label, isNotRemovable: true }]],
    },
  ];
};

const PERIOD_LABEL_IDS = new Set(['START_PERIOD', 'END_PERIOD', 'LASTNOBSERVATIONS']);

export const addI18nLabels = labels => item => {
  if (isEmpty(item)) return [];
  const transformEntry = entry => {
    if (!PERIOD_LABEL_IDS.has(entry.id)) return entry;
    const label = labels[entry.id];
    const newLabel = Array.isArray(label)
      ? intersperse(entry.label, label).join(' ')
      : `${label}: ${entry.label}`;
    return { ...entry, label: newLabel };
  };
  const first = item[0];
  const values = first.values.map(subValues => subValues.map(transformEntry));
  return [{ ...first, values }];
};
